import { ChangeEvent, ReactNode } from 'react';
import i18n from 'helpers/i18n';
import messages from '../messages';
import { MetaDataAnswer } from 'types/api/envScales/MetaDataAnswer';
import { MetaDataQuestion } from 'types/api/envScales/MetaDataQuestion';
import { renderBold, stripBold } from 'helpers/text';
import { RadioInput } from './RadioInput';
import LabelsBox, { answersBox, useIsSecondEditionPk3rd } from '../LabelsBox';
import clsx from 'clsx';

interface QuestionProps {
  question: MetaDataQuestion;
  showLabels: boolean;
  handleRadioInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  answer?: MetaDataAnswer;
  error: boolean;
  isFirstQuestion: boolean;
  /** Rendered above the question text; used to fold the content-area title into
   *  the first question so it aligns with that question's labels. */
  titleSlot?: ReactNode;
}

function Question({
  question,
  showLabels,
  handleRadioInputChange,
  answer,
  error,
  isFirstQuestion,
  titleSlot
}: QuestionProps) {
  // text has to be a plain attribute so bold tags for PK3rd only get stripped
  const plainQuestion = stripBold(question.question);
  const radioGroupName = `${plainQuestion}_${question.meta_data_choice_id}`;
  const questionTextId = `question-${question.id}`;
  const questionErrorId = `${questionTextId}-error`;
  // Word labels differ per question, so desktop can't share one row per choice.
  const isSecondEditionPk3rd = useIsSecondEditionPk3rd();

  return (
    <div
      className={clsx(
        'py-4 flex flex-wrap justify-between',
        !isFirstQuestion && 'border-t-2 border-solid border-white'
      )}
    >
      <div
        className={clsx(
          'flex flex-row justify-between w-full flex-wrap gap-3',
          titleSlot ? 'items-start' : 'items-center'
        )}
      >
        <div className="basis-[365px]">
          {titleSlot}
          <p
            className={
              titleSlot
                ? error
                  ? 'mb-0'
                  : 'mb-4'
                : error
                  ? 'mt-4 mb-0'
                  : 'my-4'
            }
            id={questionTextId}
          >
            {renderBold(question.question)}
          </p>
          {error && (
            <p id={questionErrorId} className="text-scale-red text-[14px]">
              {i18n.ft(messages.errors.required)}
            </p>
          )}
        </div>

        <div className="flex flex-col w-full lg:w-auto">
          {(showLabels || isSecondEditionPk3rd) && (
            <LabelsBox
              questionTName={question.t_name}
              questionId={question.id}
              choiceId={question.meta_data_choice_id}
            />
          )}

          <div
            className={answersBox}
            role="radiogroup"
            aria-labelledby={questionTextId}
            aria-describedby={error ? questionErrorId : undefined}
            aria-invalid={error}
          >
            <RadioInput
              id={`q${question.id}_c${question.meta_data_choice_id}_1`}
              name={radioGroupName}
              value="1"
              data-question-id={question.id}
              data-choice-id={question.meta_data_choice_id}
              data-is-first-question={isFirstQuestion}
              onChange={handleRadioInputChange}
              checked={answer?.value === 1}
              error={false}
            />
            <RadioInput
              id={`q${question.id}_c${question.meta_data_choice_id}_2`}
              name={radioGroupName}
              value="2"
              data-question-id={question.id}
              data-choice-id={question.meta_data_choice_id}
              data-is-first-question={isFirstQuestion}
              onChange={handleRadioInputChange}
              checked={answer?.value === 2}
              error={false}
            />
            <RadioInput
              id={`q${question.id}_c${question.meta_data_choice_id}_3`}
              name={radioGroupName}
              value="3"
              data-question-id={question.id}
              data-choice-id={question.meta_data_choice_id}
              data-is-first-question={isFirstQuestion}
              onChange={handleRadioInputChange}
              checked={answer?.value === 3}
              // The "Required" label above already communicates the missing
              // answer; a red radio border is redundant, so this stays false
              // regardless of `error`.
              error={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Question;
